import {Component, OnInit, Inject, DestroyRef, inject} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { FormControl, FormGroup } from '@angular/forms';
import {filter, first, map, switchMap, tap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ChangeSet} from '@emm/core/models/change-set.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ChangeSetService} from '@emm/core/services/change-set.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {API_ERRORS} from "@emm/core/util/api-constants";

@Component({
    selector: 'emm-update-change-set-dialog',
    styleUrls: ['./update-change-set-dialog.component.scss'],
    templateUrl: './update-change-set-dialog.component.html',
    standalone: false
})
export class UpdateChangeSetDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    allChangeSets: ChangeSet[];
    changeSet: any;
    dateChanged = false;
    dateChangedToToday = false;
    form: FormGroup;
    isReadOnly$ = this.authFacade.isReadOnly$;
    isReadOnlyScope: boolean;
    minDate: Date;
    disableSubmit;

    constructor(
        private authFacade: AuthFacadeService,
        private changeSetService: ChangeSetService,
        public dialogRef: MatDialogRef<UpdateChangeSetDialogComponent>,
        private notificationService: NotificationService,
        @Inject(MAT_DIALOG_DATA) public data: any) {}



    ngOnInit(): void {
        this.form = new FormGroup({
            effectiveDate: new FormControl(this.data.effectiveDate),
            name: new FormControl()
        });
        this.minDate = new Date();
        this.getChangeSet()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
        this.isReadOnlyScope = this.data.featureScope === FeatureScope.Client ||
            this.data.featureScope === FeatureScope.Viewer;
    }



    getChangeSet() {
        return this.changeSetService.getChangeSetChangesV4(this.data.featureScope, this.data.submenu,
            this.data.effectiveDate, this.data.location, 0, 10).pipe(
            first(),
            map(res => this.changeSet = res[0]),
            filter(Boolean), // find all future changesets as reference for updating current changeset date
            switchMap(() => {
                return this.getAllFutureChangeSets();
            })
        );
    }



    getAllFutureChangeSets() {
        return this.changeSetService
            .getAllFutureChangeSetsV4()
            .pipe(tap(res => (this.allChangeSets = res)));
    }



    onSubmit() {
        if (this.changeSet) {
            this.disableSubmit = true;
            this.changeSetService
                .rescheduleChangeSet(
                    this.changeSet.effectiveDate,
                    this.form.controls.effectiveDate.value,
                    this.dateChangedToToday,
                )
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe({
                    next: () => {
                        this.notificationService.success('Updated Change Set');
                        const result = {
                            action: 'updated',
                            updatedDate: this.form.controls.effectiveDate.value
                        };
                        this.dialogRef.close(result);
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                    }
                });
        }
    }



    cancelChangeSet(): void {
        this.dialogRef.close();
    }



    highlightDate = (d: Date) => MenuDateUtil.getHighlightDate(this.allChangeSets, d);



    onDateChanged(event: MatDatepickerInputEvent<Date>) {
        this.dateChanged = false;
        this.dateChangedToToday = false;
        const selectedDate = new Date(event.value);

        // skip if same as current effective date
        if (MenuDateUtil.isEqual(new Date(this.data.effectiveDate), selectedDate)) {
            return;
        }

        // show messages
        const today = MenuDateUtil.today();
        if (MenuDateUtil.isEqual(today, selectedDate)) {
            this.dateChangedToToday = true;
        } else {
            this.dateChanged = true;
        }

        // validate against existing change set dates
        const exists = this.allChangeSets.find((cs) =>
            MenuDateUtil.isEqual(new Date(cs.effectiveDate), selectedDate)
        );
        if (exists) {
            this.form.controls.effectiveDate.setErrors({ changeSetExists: true });
        }
    }



}
